<template>
  <div
    id="chart"
    tabindex="0"
    :style="{
      width: isNaN(width) ? width : width + 'px',
      height: isNaN(height) ? height : height + 'px',
      cursor: cursor,
    }"
    @mousemove="handleChartMouseMove"
    @mouseup="handleChartMouseUp"
    @dblclick="handleChartDblClick($event)"
    @mousewheel="handleChartMouseWheel"
    @mousedown="handleChartMouseDown($event)"
  >
    <span id="position" class="unselectable">
      {{ cursorToChartOffset.x + ", " + cursorToChartOffset.y }}
    </span>
    <svg id="svg">
      <rect class="selection" height="0" width="0"></rect>
    </svg>
  </div>
</template>
<style src="./index.css"></style>
<script>
import { line2, lineTo } from "../../utils/svg";
import * as d3 from "d3";
import {
  between,
  distanceOfPointToLine,
  getEdgeOfPoints,
  pointRectangleIntersection,
} from "../../utils/math";
import render from "./render";
import { getCustomConnectorPosition } from "./enhanced-render";

export default {
  name: "flowchart",
  props: {
    nodes: {
      type: Array,
      default: () => [],
    },
    connections: {
      type: Array,
      default: () => [],
    },
    width: {
      type: [String, Number],
      default: 800,
    },
    height: {
      type: [String, Number],
      default: 600,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    render: {
      type: Function,
      default: render,
    },
    createConnection: {
      type: Function,
      default: () => { return true; }
    }
  },
  data() {
    return {
      internalNodes: [],
      internalConnections: [],
      connectingInfo: {
        source: null,
        sourcePosition: null,
      },
      selectionInfo: null,
      currentNodes: [],
      currentConnections: [],
      /**
       * Mouse position(relative to chart div)
       */
      cursorToChartOffset: { x: 0, y: 0 },
      clickedOnce: false,
      pathClickedOnce: false,
      /**
       * lines of all internalConnections
       */
      lines: [],
      _debugFocusNode: null,
      _debugNodeStatuses: {},
    };
  },
  methods: {
    add(node) {
      if (this.readonly) {
        return;
      }
      this.internalNodes.push(node);
    },
    editCurrent() {
      if (this.currentNodes.length === 1) {
        this.editNode(this.currentNodes[0]);
      } else if (this.currentConnections.length === 1) {
        this.editConnection(this.currentConnections[0]);
      }
    },
    editNode(node) {
      if (this.readonly) {
        return;
      }
      this.$emit("editnode", node);
    },
    editConnection(connection) {
      if (this.readonly) {
        return;
      }
      this.$emit("editconnection", connection);
    },
    handleChartMouseWheel(event) {
      event.stopPropagation();
      event.preventDefault();
      if (event.ctrlKey) {
        let svg = document.getElementById("svg");
        let zoom = parseFloat(svg.style.zoom || 1);
        if (event.deltaY > 0 && zoom === 0.1) {
          return;
        }
        zoom -= event.deltaY / 100 / 10;
        svg.style.zoom = zoom;
      }
    },
    async handleChartMouseUp() {
      if (this.connectingInfo.source) {
        if (this.readonly) {
          this.connectingInfo.source = null;
          this.connectingInfo.sourcePosition = null;
          return;
        }
        
        if (this.hoveredConnector) {
          if (this.connectingInfo.source.id !== this.hoveredConnector.node.id) {
            // Node can't connect to itself
            let tempId = +new Date();
            let conn = {
              source: {
                id: this.connectingInfo.source.id,
                position: this.connectingInfo.sourcePosition,
              },
              destination: {
                id: this.hoveredConnector.node.id,
                position: this.hoveredConnector.position,
              },
              id: tempId,
              type: "pass",
              name: "Pass",
            };

            if (this.createConnection(conn, this.internalConnections, this.internalNodes)) {
              const exists = this.internalConnections.some(c => c.id === conn.id);
              if (!exists) {
                this.internalConnections.push(conn);
              }
            }
          }
        }
        this.connectingInfo.source = null;
        this.connectingInfo.sourcePosition = null;
      }
      if (this.selectionInfo) {
        this.selectionInfo = null;
      }
    },
    async handleChartMouseMove(event) {
      // calc offset of cursor to chart
      let boundingClientRect = event.currentTarget.getBoundingClientRect();
      let actualX = event.pageX - boundingClientRect.left - window.scrollX;
      this.cursorToChartOffset.x = Math.trunc(actualX);
      let actualY = event.pageY - boundingClientRect.top - window.scrollY;
      this.cursorToChartOffset.y = Math.trunc(actualY);

      if (this.connectingInfo.source) {
        await this.renderConnections();

        d3.selectAll("#svg .connector").classed("active", true);

        let sourceOffset = this.getNodeConnectorOffset(
          this.connectingInfo.source.id,
          this.connectingInfo.sourcePosition
        );
        let destinationPosition = this.hoveredConnector
          ? this.hoveredConnector.position
          : null;

        let node = this.hoveredConnector !== null
          ? this.hoveredConnector.node 
          : null;

        this.arrowTo(
          sourceOffset.x,
          sourceOffset.y,
          this.cursorToChartOffset.x,
          this.cursorToChartOffset.y,
          this.connectingInfo.sourcePosition,
          destinationPosition
        );
      }
    },
    handleChartDblClick(event) {
      if (this.readonly) {
        return;
      }
      this.$emit("dblclick", { x: event.offsetX, y: event.offsetY });
    },
    handleChartMouseDown(event) {
      if (event.ctrlKey) {
        return;
      }
      this.selectionInfo = { x: event.offsetX, y: event.offsetY };
    },
    getConnectorPosition(node) {
      // Use custom connector position for different node shapes
      return getCustomConnectorPosition(node);
    },
    renderSelection() {
      let that = this;
      // render selection rectangle
      if (that.selectionInfo) {
        that.currentNodes.splice(0, that.currentNodes.length);
        that.currentConnections.splice(0, that.currentConnections.length);
        let edge = getEdgeOfPoints([
          { x: that.selectionInfo.x, y: that.selectionInfo.y },
          { x: that.cursorToChartOffset.x, y: that.cursorToChartOffset.y },
        ]);
        let svg = d3.select("#svg");
        let rect = svg.select(".selection").classed("active", true);
        rect
          .attr("x", edge.start.x)
          .attr("y", edge.start.y)
          .attr("width", edge.end.x - edge.start.x)
          .attr("height", edge.end.y - edge.start.y);

        that.internalNodes.forEach((item) => {
          let points = [
            { x: item.x, y: item.y },
            { x: item.x, y: item.y + item.height },
            { x: item.x + item.width, y: item.y },
            { x: item.x + item.width, y: item.y + item.height },
          ];
          if (
            points.every((point) => pointRectangleIntersection(point, edge))
          ) {
            that.currentNodes.push(item);
          }
        });
        that.lines.forEach((line) => {
          let points = [
            { x: line.sourceX, y: line.sourceY },
            { x: line.destinationX, y: line.destinationY },
          ];
          if (
            points.every((point) => pointRectangleIntersection(point, edge)) &&
            that.currentConnections.every((item) => item.id !== line.id)
          ) {
            let connection = that.internalConnections.filter(
              (conn) => conn.id === line.id
            )[0];
            that.currentConnections.push(connection);
          }
        });
      } else {
        d3.selectAll("#svg > .selection").classed("active", false);
      }
    },
    
    renderConnections() {
      let that = this;
      return new Promise(function (resolve) {
        that.$nextTick(function () {
          d3.selectAll("#svg > g.connection").remove();
          // render lines
          that.lines = [];
          that.internalConnections.forEach((conn) => {
            let sourcePosition = that.getNodeConnectorOffset(
              conn.source.id,
              conn.source.position
            );
            let destinationPosition = that.getNodeConnectorOffset(
              conn.destination.id,
              conn.destination.position
            );

            let colors = {
              pass: "#52c41a",
              reject: "red",
            };
            if (
              that.currentConnections.filter((item) => item === conn).length > 0
            ) {
              colors = {
                pass: "#12640a",
                reject: "darkred",
              };
            }
            let result = that.arrowTo(
              sourcePosition.x,
              sourcePosition.y,
              destinationPosition.x,
              destinationPosition.y,
              conn.source.position,
              conn.destination.position,
              colors[conn.type],
              conn.name
            );
            result.bindTo.attr('data-source-id', conn.source.id).attr('data-dest-id', conn.destination.id);
            for (const path of result.paths) {
              path.on("mousedown", function () {
                d3.event.stopPropagation();
                if (that.pathClickedOnce) {
                  that.editConnection(conn);
                } else {
                  let timer = setTimeout(function () {
                    that.pathClickedOnce = false;
                    clearTimeout(timer);
                  }, 300);
                  that.pathClickedOnce = true;
                }
                that.currentNodes.splice(0, that.currentNodes.length);
                that.currentConnections.splice(
                  0,
                  that.currentConnections.length
                );
                that.currentConnections.push(conn);
              });
            }
            for (const line of result.lines) {
              that.lines.push({
                sourceX: line.sourceX,
                sourceY: line.sourceY,
                destinationX: line.destinationX,
                destinationY: line.destinationY,
                id: conn.id,
              });
            }
          });
          that.reapplyDebugState();
          resolve();
        });
      });
    },
    renderNodes() {
      let that = this;
      return new Promise(function (resolve) {
        d3.selectAll("#svg > g.node").remove();

        // render nodes
        that.internalNodes.forEach((node) => {
          that.renderNode(
            node,
            that.currentNodes.filter((item) => item === node).length > 0
          );
        });

        that.reapplyDebugState();
        resolve();
      });
    },
    getNodeConnectorOffset(nodeId, connectorPosition) {
      let node = this.internalNodes.filter((item) => item.id === nodeId)[0];
      if (!node) {
        console.warn('getNodeConnectorOffset: Node not found with id:', nodeId);
        return { x: 0, y: 0 };
      }
      return this.getConnectorPosition(node)[connectorPosition];
    },
    append(element) {
      let svg = d3.select("#svg");
      return svg.insert(element, ".selection");
    },
    guideLineTo(x1, y1, x2, y2) {
      let g = this.append("g");
      g.classed("guideline", true);
      lineTo(g, x1, y1, x2, y2, 1, "#a3a3a3", [5, 3]);
    },
    arrowTo(x1, y1, x2, y2, startPosition, endPosition, color, name) {
      let g = this.append("g");
      g.classed("connection", true);
      line2(
        g,
        x1,
        y1,
        x2,
        y2,
        startPosition,
        endPosition,
        1,
        color || "#a3a3a3",
        true,
        name
      );

      // a 5px cover to make mouse operation conveniently
      let result = line2(
        g,
        x1,
        y1,
        x2,
        y2,
        startPosition,
        endPosition,
        5,
        "transparent",
        false,
        name
      );
      result.bindTo = g;
      return result;
    },
    renderNode(node, isSelected) {
      let that = this;
      let g = that.append("g").attr("cursor", "move").classed("node", true).attr("data-node-id", node.id);

      node.render = that.render;
      node.render(g, node, isSelected);

      // Wire play button click
      g.selectAll('.node-play-btn').on('mousedown', function () {
        d3.event.stopPropagation();
      }).on('click', function () {
        d3.event.stopPropagation();
        that.$emit('playnode', node);
      });

      let drag = d3
        .drag()
        .on("start", function () {
          // Prevent dragging in readonly mode
          if (that.readonly) {
            return;
          }
          
          // handle mousedown
          let isNotCurrentNode =
            that.currentNodes.filter((item) => item === node).length === 0;
          if (isNotCurrentNode) {
            that.currentConnections.splice(0, that.currentConnections.length);
            that.currentNodes.splice(0, that.currentNodes.length);
            that.currentNodes.push(node);
          }

          if (that.clickedOnce) {
            that.currentNodes.splice(0, that.currentNodes.length);
            that.editNode(node);
          } else {
            let timer = setTimeout(function () {
              that.clickedOnce = false;
              clearTimeout(timer);
            }, 300);
            that.clickedOnce = true;
          }

          // Activate dashed animated connections for the dragged node
          that.activateDragConnections(node);
        })
        .on("drag", async function () {
          if (that.readonly) {
            return;
          }

          let zoom = parseFloat(document.getElementById("svg").style.zoom || 1);
          for (let currentNode of that.currentNodes) {
            let x = d3.event.dx / zoom;
            if (currentNode.x + x < 0) {
              x = -currentNode.x;
            }
            currentNode.x += x;
            let y = d3.event.dy / zoom;
            if (currentNode.y + y < 0) {
              y = -currentNode.y;
            }
            currentNode.y += y;
          }

          // Re-render connections during drag
          await that.renderConnections();
          // Re-apply drag animation after connections are re-rendered
          that.activateDragConnections(that.currentNodes[0]);

          d3.selectAll("#svg > g.guideline").remove();
          let edge = that.getCurrentNodesEdge();
          let expectX = Math.round(Math.round(edge.start.x) / 10) * 10;
          let expectY = Math.round(Math.round(edge.start.y) / 10) * 10;
          that.internalNodes.forEach((item) => {
            if (
              that.currentNodes.filter((currentNode) => currentNode === item)
                .length === 0
            ) {
              if (item.x === expectX) {
                // vertical guideline
                if (item.y < expectY) {
                  that.guideLineTo(
                    item.x,
                    item.y + item.height,
                    expectX,
                    expectY
                  );
                } else {
                  that.guideLineTo(
                    expectX,
                    expectY + item.height,
                    item.x,
                    item.y
                  );
                }
              }
              if (item.y === expectY) {
                // horizontal guideline
                if (item.x < expectX) {
                  that.guideLineTo(
                    item.x + item.width,
                    item.y,
                    expectX,
                    expectY
                  );
                } else {
                  that.guideLineTo(
                    expectX + item.width,
                    expectY,
                    item.x,
                    item.y
                  );
                }
              }
            }
          });
        })
        .on("end", function () {
          if (that.readonly) {
            return;
          }
          
          d3.selectAll("#svg > g.guideline").remove();
          for (let currentNode of that.currentNodes) {
            currentNode.x = Math.round(Math.round(currentNode.x) / 10) * 10;
            currentNode.y = Math.round(Math.round(currentNode.y) / 10) * 10;
          }

          // Restore normal connections after drag
          that.deactivateDragConnections();

          that.$emit("dragend");
        });
      g.call(drag);
      g.on("mousedown", function () {
        // handle ctrl+mousedown
        if (!d3.event.ctrlKey) {
          return;
        }
        let isNotCurrentNode =
          that.currentNodes.filter((item) => item === node).length === 0;
        if (isNotCurrentNode) {
          that.currentNodes.push(node);
        } else {
          that.currentNodes.splice(that.currentNodes.indexOf(node), 1);
        }
      });

      let connectors = [];
      let connectorPosition = this.getConnectorPosition(node);
      for (let position in connectorPosition) {
        let positionElement = connectorPosition[position];
        let connector = g
          .append("circle")
          .attr("cx", positionElement.x)
          .attr("cy", positionElement.y)
          .attr("r", 4)
          .attr("class", "connector");
        connector
          .on("mousedown", function () {
            d3.event.stopPropagation();
            if (node.type === "end" || that.readonly) {
              return;
            }
            that.connectingInfo.source = node;
            that.connectingInfo.sourcePosition = position;

            that.$emit("connectingInfo", {node: node, position: position});
          })
          .on("mouseup", function () {
            d3.event.stopPropagation();
            if (that.readonly) {
              return;
            }
            
            if (that.connectingInfo.source) {
              if (that.connectingInfo.source.id !== node.id) {
                // Node can't connect to itself
                let tempId = +new Date();
                let conn = {
                  source: {
                    id: that.connectingInfo.source.id,
                    position: that.connectingInfo.sourcePosition,
                  },
                  destination: {
                    id: node.id,
                    position: position,
                  },
                  id: tempId,
                  type: "pass",
                  name: "Pass",
                };

                if (that.createConnection(conn, that.internalConnections, that.internalNodes)) {
                  const exists = that.internalConnections.some(c => c.id === conn.id);
                  if (!exists) {
                    that.internalConnections.push(conn);
                  }
                }
              }
              that.connectingInfo.source = null;
              that.connectingInfo.sourcePosition = null;
            }
          })
          .on("mouseover", function () {
            connector.classed("active", true);
          })
          .on("mouseout", function () {
            connector.classed("active", false);
          });
        connectors.push(connector);
      }
      g.on("mouseover", function () {
        connectors.forEach((conn) => conn.classed("active", true));
      }).on("mouseout", function () {
        connectors.forEach((conn) => conn.classed("active", false));
      });
    },
    getCurrentNodesEdge() {
      let points = this.currentNodes.map((node) => ({
        x: node.x,
        y: node.y,
      }));
      points.push(
        ...this.currentNodes.map((node) => ({
          x: node.x + node.width,
          y: node.y + node.height,
        }))
      );
      return getEdgeOfPoints(points);
    },
    save() {
      if (this.readonly) {
        return;
      }
      this.$emit("save", this.internalNodes, this.internalConnections);
    },
    async remove() {
      if (this.readonly) {
        return;
      }
      if (this.currentConnections.length > 0) {
        for (let conn of this.currentConnections) {
          this.removeConnection(conn);
        }
        this.currentConnections.splice(0, this.currentConnections.length);
      }
      if (this.currentNodes.length > 0) {
        for (let node of this.currentNodes) {
          this.removeNode(node);
        }
        this.currentNodes.splice(0, this.currentNodes.length);
      }
    },
    removeNode(node) {
      let connections = this.internalConnections.filter(
        (item) => item.source.id === node.id || item.destination.id === node.id
      );
      for (let connection of connections) {
        this.internalConnections.splice(
          this.internalConnections.indexOf(connection),
          1
        );
      }
      this.internalNodes.splice(this.internalNodes.indexOf(node), 1);
    },
    removeConnection(conn) {
      let index = this.internalConnections.indexOf(conn);
      this.internalConnections.splice(index, 1);
    },
    moveCurrentNode(x, y) {
      if (this.currentNodes.length > 0 && !this.readonly) {
        for (let node of this.currentNodes) {
          if (node.x + x < 0) {
            x = -node.x;
          }
          node.x += x;
          if (node.y + y < 0) {
            y = -node.y;
          }
          node.y += y;
        }
      }
    },
    init() {
      let that = this;
      that.internalNodes.splice(0, that.internalNodes.length);
      that.internalConnections.splice(0, that.internalConnections.length);
      that.nodes.forEach((node) => {
        let newNode = Object.assign({}, node);
        newNode.width = newNode.width || 120;
        newNode.height = newNode.height || 60;
        that.internalNodes.push(newNode);
      });
      that.connections.forEach((connection) => {
        that.internalConnections.push(JSON.parse(JSON.stringify(connection)));
      });
    },
    
    /**
     * Activate dashed animated connections for a node being dragged
     */
    activateDragConnections(node) {
      // Find all connections related to this node (and any other currently selected nodes)
      const draggedNodeIds = this.currentNodes.map(n => n.id);
      
      const relatedConnections = this.internalConnections.filter(conn => 
        draggedNodeIds.includes(conn.source.id) || draggedNodeIds.includes(conn.destination.id)
      );
      
      // Store reference to related connections for later use
      this._draggedConnections = relatedConnections;
      
      // Apply animation only to related connections by matching with connection index
      d3.selectAll("#svg > g.connection").each((d, i) => {
        // Check if this connection index corresponds to a related connection
        const connection = this.internalConnections[i];
        if (connection && relatedConnections.some(relConn => 
          relConn.source.id === connection.source.id && 
          relConn.destination.id === connection.destination.id
        )) {
          // Apply dashed animation to all paths in this connection group
          d3.select(d3.selectAll("#svg > g.connection").nodes()[i])
            .selectAll('path')
            .style('stroke-dasharray', '8,4')
            .style('stroke-dashoffset', 0)
            .style('opacity', 0.7)
            .attr('data-dragging', 'true')
            .attr('data-connection-id', `${connection.source.id}-${connection.destination.id}`)
            .transition()
            .duration(1000)
            .ease(d3.easeLinear)
            .style('stroke-dashoffset', -12)
            .on('end', function repeat() {
              const isDragging = d3.select(this).attr('data-dragging') === 'true';
              if (isDragging) {
                d3.select(this)
                  .style('stroke-dashoffset', 0)
                  .transition()
                  .duration(1000)
                  .ease(d3.easeLinear)
                  .style('stroke-dashoffset', -12)
                  .on('end', repeat);
              }
            });
        }
      });
    },
    
    /**
     * Restore normal connections after drag ends
     */
    deactivateDragConnections() {
      // Remove dashed style and stop animations
      d3.selectAll("#svg > g.connection path")
        .interrupt() // Stop any ongoing transitions
        .style('stroke-dasharray', 'none')
        .style('stroke-dashoffset', 0)
        .style('opacity', 1)
        .attr('data-dragging', null);
    },

    getReachableNodeIds(eventNode) {
      let ids = new Set([eventNode.id]);
      let queue = [eventNode];
      while (queue.length > 0) {
        let current = queue.shift();
        if (current.connections) {
          for (let conn of current.connections) {
            let destId = conn.destination ? conn.destination.id : (conn.id || null);
            if (destId && !ids.has(destId)) {
              ids.add(destId);
              let child = this.internalNodes.find(n => n.id === destId);
              if (child) queue.push(child);
            }
          }
        }
      }
      return ids;
    },

    reapplyDebugState() {
      if (this._debugFocusNode) {
        this.focusOnNode(this._debugFocusNode, true, true);
      }
      if (!this._debugNodeStatuses) this._debugNodeStatuses = {};
      let statuses = this._debugNodeStatuses;
      for (let nodeId in statuses) {
        this.updateNodeDebugStatus(parseInt(nodeId), statuses[nodeId]);
      }
    },

    focusOnNode(eventNode, skipCenter, instant) {
      this._debugFocusNode = eventNode;
      let reachableIds = this.getReachableNodeIds(eventNode);

      d3.selectAll('#svg > g.node').each(function() {
        let nodeId = parseInt(d3.select(this).attr('data-node-id'));
        let el = d3.select(this);
        if (instant) {
          el.style('opacity', reachableIds.has(nodeId) ? 1 : 0.08);
        } else {
          el.transition().duration(400)
            .style('opacity', reachableIds.has(nodeId) ? 1 : 0.08);
        }
      });
      d3.selectAll('#svg > g.connection').each(function() {
        let srcId = parseInt(d3.select(this).attr('data-source-id'));
        let dstId = parseInt(d3.select(this).attr('data-dest-id'));
        let visible = reachableIds.has(srcId) && reachableIds.has(dstId);
        let el = d3.select(this);
        if (instant) {
          el.style('opacity', visible ? 1 : 0.05);
        } else {
          el.transition().duration(400)
            .style('opacity', visible ? 1 : 0.05);
        }
      });

      if (skipCenter) return;

      let chart = document.getElementById('chart');
      let svg = document.getElementById('svg');
      if (chart && svg) {
        let zoom = parseFloat(svg.style.zoom || 1);
        let nodeW = eventNode.width || 200;
        let nodeH = eventNode.height || 60;
        let nodeCenterX = (eventNode.x + nodeW / 2) * zoom;
        let nodeCenterY = (eventNode.y + nodeH / 2) * zoom;

        this._savedOverflow = chart.style.overflow;
        chart.style.overflow = 'auto';

        // SVG needs to be large enough to scroll to
        let svgW = svg.getBoundingClientRect().width;
        let svgH = svg.getBoundingClientRect().height;
        svg.style.minWidth = Math.max(svgW, nodeCenterX + chart.clientWidth) + 'px';
        svg.style.minHeight = Math.max(svgH, nodeCenterY + chart.clientHeight) + 'px';

        this.$nextTick(() => {
          chart.scrollTo({
            left: nodeCenterX - chart.clientWidth / 2,
            top: nodeCenterY - chart.clientHeight / 2,
            behavior: 'smooth'
          });
        });
      }
    },

    clearFocus() {
      this._debugFocusNode = null;
      this._debugNodeStatuses = {};
      d3.selectAll('#svg > g.node')
        .transition().duration(400)
        .style('opacity', 1);
      d3.selectAll('#svg > g.connection')
        .transition().duration(400)
        .style('opacity', 1);
      this.clearNodeDebugStatus();

      let chart = document.getElementById('chart');
      let svg = document.getElementById('svg');
      if (chart) {
        chart.style.overflow = this._savedOverflow || 'hidden';
      }
      if (svg) {
        svg.style.minWidth = '';
        svg.style.minHeight = '';
      }
    },

    updateNodeDebugStatus(nodeId, status) {
      if (!this._debugNodeStatuses) this._debugNodeStatuses = {};
      this._debugNodeStatuses[nodeId] = status;
      let statusColors = {
        running:   { bg: '#1e3a5f', border: '#60a5fa', text: '#93c5fd' },
        completed: { bg: '#14532d', border: '#4ade80', text: '#86efac' },
        errored:   { bg: '#7f1d1d', border: '#f87171', text: '#fca5a5' },
        skipped:   { bg: '#374151', border: '#9ca3af', text: '#d1d5db' },
        pending:   { bg: '#713f12', border: '#facc15', text: '#fde68a' },
      };
      let colors = statusColors[status] || statusColors.skipped;

      d3.selectAll('#svg > g.node').each(function() {
        let nid = parseInt(d3.select(this).attr('data-node-id'));
        if (nid !== nodeId) return;

        let g = d3.select(this);
        g.selectAll('.debug-overlay').remove();

        let rects = g.selectAll('.n8n-dark-node rect').nodes();
        if (rects.length < 2) return;
        let bgRect = d3.select(rects[0]);
        let overlayRect = d3.select(rects[1]);
        let bbox = overlayRect.node().getBBox();

        // Save original styles for restore
        if (!g.attr('data-orig-fill')) {
          g.attr('data-orig-fill', overlayRect.attr('fill'));
          g.attr('data-orig-stroke', bgRect.attr('stroke'));
          g.attr('data-orig-stroke-w', bgRect.attr('stroke-width'));
          g.attr('data-orig-overlay-stroke', overlayRect.attr('stroke') || 'none');
          g.attr('data-orig-overlay-stroke-w', overlayRect.attr('stroke-width') || '0');
        }

        // Change the node background color to status tint
        overlayRect
          .attr('fill', colors.bg)
          .attr('opacity', 0.97)
          .attr('stroke', colors.border)
          .attr('stroke-width', 3);

        // Change gradient rect border too
        bgRect
          .attr('stroke', colors.border)
          .attr('stroke-width', 3);

        // Status label pill above the node
        let labelText = status.toUpperCase();
        let labelW = labelText.length * 9 + 24;
        let labelX = bbox.x + bbox.width / 2 - labelW / 2;
        let labelY = bbox.y - 32;

        g.append('rect')
          .attr('class', 'debug-overlay')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('width', labelW)
          .attr('height', 24)
          .attr('rx', 12)
          .attr('fill', colors.border)
          .attr('pointer-events', 'none');

        g.append('text')
          .attr('class', 'debug-overlay')
          .attr('x', bbox.x + bbox.width / 2)
          .attr('y', labelY + 16)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ffffff')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('letter-spacing', '1px')
          .attr('pointer-events', 'none')
          .text(labelText);
      });
    },

    clearNodeDebugStatus() {
      d3.selectAll('#svg > g.node').each(function() {
        let g = d3.select(this);
        g.selectAll('.debug-overlay').remove();

        if (!g.attr('data-orig-fill')) return;

        let rects = g.selectAll('.n8n-dark-node rect').nodes();
        if (rects.length >= 2) {
          let bgRect = d3.select(rects[0]);
          let overlayRect = d3.select(rects[1]);
          overlayRect.attr('fill', g.attr('data-orig-fill'))
            .attr('opacity', 0.95)
            .attr('stroke', g.attr('data-orig-overlay-stroke') === 'none' ? null : g.attr('data-orig-overlay-stroke'))
            .attr('stroke-width', g.attr('data-orig-overlay-stroke-w') || null);
          bgRect.attr('stroke', g.attr('data-orig-stroke'))
            .attr('stroke-width', g.attr('data-orig-stroke-w'));
        }
        g.attr('data-orig-fill', null)
          .attr('data-orig-stroke', null)
          .attr('data-orig-stroke-w', null)
          .attr('data-orig-overlay-stroke', null)
          .attr('data-orig-overlay-stroke-w', null);
      });
    }
  },
  mounted() {
    let that = this;
    that.init();

    document.onkeydown = function (event) {
      switch (event.keyCode) {
        case 65:
          if (document.activeElement === document.getElementById("chart")) {
            that.currentNodes.splice(0, that.currentNodes.length);
            that.currentConnections.splice(0, that.currentConnections.length);
            that.currentNodes.push(...that.internalNodes);
            that.currentConnections.push(...that.internalConnections);
            event.preventDefault();
          }
          break;
        case 46:
          that.remove();
          break;
        default:
          break;
      }
    }
    
    let svg = d3.select("#svg");

    svg.call(d3.drag().on("drag", function() {
      let marginLeft = parseInt(svg.style("margin-left"))
      let marginTop = parseInt(svg.style("margin-top"))

      let cMarginLeft = marginLeft + d3.event.dx
      let cMarginTop = marginTop + d3.event.dy

      if (cMarginLeft < 0) {
        svg.style("margin-left", `${cMarginLeft}px`)
        let width = parseInt(svg.style("width"))

        let b = d3.event.dx
        if (b < 0) {
          b = Math.abs(b)
        }

        svg.style("width", `${width + b}px`)
      }

      if (cMarginTop < 0) {
        svg.style("margin-top", `${cMarginTop}px`)
        let height = parseInt(svg.style("height"))

        let b = d3.event.dx
        if (b < 0) {
          b = Math.abs(b)
        }

        svg.style("height", `${height + b}px`)
      }


      //svg.style("width", ${width})
    }));
  },
  created() { },
  computed: {
    hoveredConnector() {
      for (const node of this.internalNodes) {
        let connectorPosition = this.getConnectorPosition(node);
        for (let prop in connectorPosition) {
          let entry = connectorPosition[prop];
          if (
            Math.hypot(
              entry.x - this.cursorToChartOffset.x,
              entry.y - this.cursorToChartOffset.y
            ) < 10
          ) {
            return { position: prop, node: node };
          }
        }
      }
      return null;
    },
    hoveredConnection() {
      for (const line of this.lines) {
        let distance = distanceOfPointToLine(
          line.sourceX,
          line.sourceY,
          line.destinationX,
          line.destinationY,
          this.cursorToChartOffset.x,
          this.cursorToChartOffset.y
        );
        if (
          distance < 5 &&
          between(
            line.sourceX - 2,
            line.destinationX + 2,
            this.cursorToChartOffset.x
          ) &&
          between(
            line.sourceY - 2,
            line.destinationY + 2,
            this.cursorToChartOffset.y
          )
        ) {
          let connections = this.internalConnections.filter(
            (item) => item.id === line.id
          );
          return connections.length > 0 ? connections[0] : null;
        }
      }
      return null;
    },
    cursor() {
      if (this.connectingInfo.source || this.hoveredConnector) {
        return "crosshair";
      }
      if (this.hoveredConnection != null) {
        return "pointer";
      }
      return null;
    },
  },
  watch: {
    internalNodes: {
      immediate: true,
      deep: true,
      handler() {
        this.renderNodes();
        this.renderConnections();
      },
    },
    internalConnections: {
      immediate: true,
      deep: true,
      handler() {
        this.renderConnections();
      },
    },
    selectionInfo: {
      immediate: true,
      deep: true,
      handler() {}
    },
    currentNodes: {
      immediate: true,
      deep: true,
      handler() {
        this.renderNodes();
      },
    },
    currentConnections: {
      immediate: true,
      deep: true,
      handler() {
        this.renderConnections();
      },
    },
    cursorToChartOffset: {
      immediate: true,
      deep: true,
      handler() {},
    },
    connectingInfo: {
      immediate: true,
      deep: true,
      handler() {
        this.renderConnections();
      },
    },
    nodes: {
      immediate: true,
      deep: true,
      handler() {
        this.init();
      },
    },
    connections: {
      immediate: true,
      deep: true,
      handler() {
        this.init();
      },
    },
  },
};
</script>
